'use client';

import { Input as DSInput, Textarea as DSTextarea } from '@lulalife/design-system';
import { Eye, EyeOff } from '@lulalife/design-system/icons/line';
import get from 'lodash/get';
import { type ChangeEvent, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

export interface InputFieldProps {
  name: string;
  /** `'textarea'` renders DS `Textarea` (with `rows`); every other value renders DS `Input`, same split the reactstrap version made internally. */
  type: string;
  placeholder?: string;
  value?: string;
  className?: string;
  disabled?: boolean;
  autoComplete?: string;
  label?: string;
  mandatory?: boolean;
  readOnly?: boolean;
  hideFormError?: boolean;
  /** Static helper text, passed straight through to DS `Input`/`Textarea`'s own `hint` — which those components replace with the field's error message once one appears. Omit for fields with no static description. */
  hint?: string;
  /** Overrides the id/htmlFor pairing, which otherwise defaults to `name` — same as the legacy `InputField`. */
  id?: string;
  /** Only meaningful when `type="textarea"`. */
  rows?: number;
  customHandleChange?: (inputEvent: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  /** Spread onto the underlying control in the legacy `InputField` too (via its rest-prop passthrough) — used for the auto-growing description textarea's click-to-focus handling. */
  onClick?: (e: React.MouseEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  'data-testid'?: string;
  'aria-label'?: string;
}

/**
 * Same `name`-bound, `Controller`-wired API as
 * `src/components/Shared/FormFields/InputField.tsx` — only the rendered
 * control changed, from a reactstrap `Input`/`FormGroup`/`Label` to the design
 * system's `Input`. A call site swaps the import and keeps every prop it
 * already passes; the field still connects to the form the same way,
 * including `type="password"`'s show/hide toggle (Login, Reset/Set Password,
 * the account editor's password field all rely on it).
 *
 * Deliberately not carried over from `InputField`: `formGroupClass`, `size`,
 * `labelClass`, `inputGroupItems` (zero real call sites pass it — only the
 * legacy component's own password toggle used the underlying `InputGroup`),
 * and `tooltipId`/`tooltipContent`. Those are reactstrap `FormGroup`/
 * `InputGroup` concerns — the design system's `Input` is not wrapped in
 * either, so there is no equivalent slot to carry them into. A settings row
 * already draws its own label, so `label` here renders through DS `Input`'s
 * own optional label rather than a separate reactstrap `Label` element.
 *
 * Also checked and confirmed inapplicable: `checkVendorDomain`'s `wo-jc`
 * class toggle. It targeted a Bootstrap grid layout hook the design system
 * does not share, so there is nothing for it to attach to here.
 */
export const InputField = (props: InputFieldProps) => {
  const {
    name,
    type,
    placeholder = '',
    value,
    label,
    mandatory = false,
    className = '',
    readOnly = false,
    hideFormError = false,
    hint,
    disabled,
    autoComplete,
    id,
    rows,
    customHandleChange,
    onBlur,
    onClick,
    'data-testid': dataTestId,
    'aria-label': ariaLabel,
  } = props;

  const {
    control,
    formState: { errors },
  } = useFormContext();

  // Only relevant when `type === 'password'`; unused (and harmless) otherwise.
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const fieldError = get(errors, name);
  const fieldErrorMessage =
    typeof fieldError?.message === 'string' ? fieldError.message : undefined;
  const resolvedLabel = mandatory && label ? `${label} *` : label;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) =>
        type === 'textarea' ? (
          <DSTextarea
            id={id ?? name}
            rows={rows}
            label={resolvedLabel}
            placeholder={placeholder}
            className={className}
            value={value ?? field?.value ?? ''}
            disabled={disabled}
            readOnly={readOnly}
            aria-label={ariaLabel}
            hint={hint}
            // DS Textarea's `error` is the message string itself, not a
            // boolean + separate `errorMessage` pair like DS `Input` — one
            // prop covers both here.
            error={hideFormError ? undefined : fieldErrorMessage}
            data-testid={dataTestId ?? `${name}-input`}
            onClick={onClick}
            onChange={(inputChangeEvent: ChangeEvent<HTMLTextAreaElement>) => {
              if (customHandleChange) {
                field.onChange(customHandleChange(inputChangeEvent));
              } else {
                field.onChange(inputChangeEvent);
              }
            }}
            onBlur={(event: React.FocusEvent<HTMLTextAreaElement>) => {
              field.onBlur();
              onBlur?.(event);
            }}
          />
        ) : (
          <DSInput
            id={id ?? name}
            type={type === 'password' && isPasswordVisible ? 'text' : type}
            label={resolvedLabel}
            placeholder={placeholder}
            className={className}
            value={value ?? field?.value ?? ''}
            disabled={disabled}
            readOnly={readOnly}
            autoComplete={autoComplete}
            aria-label={ariaLabel}
            hint={hint}
            error={hideFormError ? false : Boolean(fieldError)}
            errorMessage={hideFormError ? undefined : fieldErrorMessage}
            data-testid={dataTestId ?? `${name}-input`}
            rightIcon={
              type === 'password' ? (
                <button
                  type="button"
                  className="flex"
                  aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
                  disabled={disabled}
                  onClick={() => setIsPasswordVisible((visible) => !visible)}
                >
                  {isPasswordVisible ? (
                    <EyeOff aria-hidden="true" size={16} />
                  ) : (
                    <Eye aria-hidden="true" size={16} />
                  )}
                </button>
              ) : undefined
            }
            onClick={onClick}
            onChange={(inputChangeEvent: ChangeEvent<HTMLInputElement>) => {
              if (customHandleChange) {
                field.onChange(customHandleChange(inputChangeEvent));
              } else {
                field.onChange(inputChangeEvent);
              }
            }}
            onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
              field.onBlur();
              onBlur?.(event);
            }}
          />
        )
      }
    />
  );
};

export default InputField;
